// Kiosk chrome — full-viewport frame with a persistent top bar (home, brand,
// weighbridge name, staff name, live clock, theme toggle, settings + logout)
// and an optional amber SIMULATOR banner. No sidebar, no app topnav.
//
// The whole frame carries `data-kiosk-theme` (dark | light) so every kiosk
// component resolves its colours from the `var(--kiosk-*)` tokens defined for
// that theme in kiosk.module.css.

import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKioskTheme } from './hooks/useKioskTheme';
import styles from './kiosk.module.css';

export const KIOSK_SIMULATOR_MODE =
  (import.meta.env.VITE_WB_MOCK_AGENT_MODE as string | undefined) === 'true';

function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <span className={styles.clock} aria-label="Current time">
      {now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
    </span>
  );
}

export interface KioskLayoutProps {
  readonly weighbridgeName?: string | null;
  readonly staffName?: string | null;
  readonly onLogout?: () => void;
  readonly onSettings?: () => void;
  /** Return to the ticket list. When provided AND `showHome`, a home button
   *  renders at the far left of the top bar. */
  readonly onHome?: () => void;
  /** Hide the home button on the ticket list itself (nothing to go back to). */
  readonly showHome?: boolean;
  readonly children: ReactNode;
}

export function KioskLayout({
  weighbridgeName,
  staffName,
  onLogout,
  onSettings,
  onHome,
  showHome = false,
  children,
}: KioskLayoutProps) {
  const { theme, toggle } = useKioskTheme();
  const navigate = useNavigate();

  return (
    <div className={styles.root} data-kiosk-theme={theme}>
      {KIOSK_SIMULATOR_MODE ? (
        <div className={styles.simBanner} role="status">
          ● Simulator mode
        </div>
      ) : null}
      <header className={styles.topbar}>
        {/* Leave the kiosk → main module selector. Always available. */}
        <button
          type="button"
          className={styles.homeButton}
          onClick={() => navigate('/')}
          aria-label="Home"
          title="Main menu"
        >
          ⌂ Home
        </button>
        {onHome && showHome ? (
          <button
            type="button"
            className={styles.homeButton}
            onClick={onHome}
            aria-label="Back to tickets"
          >
            ← Tickets
          </button>
        ) : null}
        <span className={styles.brand}>TRICIVO</span>
        {weighbridgeName ? (
          <div className={styles.topMeta}>
            <span className={styles.topMetaLabel}>Weighbridge</span>
            <span className={styles.topMetaValue}>{weighbridgeName}</span>
          </div>
        ) : null}
        <div className={styles.spacer} />
        {staffName ? (
          <div className={styles.topMeta}>
            <span className={styles.topMetaLabel}>Operator</span>
            <span className={styles.topMetaValue}>{staffName}</span>
          </div>
        ) : null}
        <Clock />
        <button
          type="button"
          className={styles.iconBtn}
          onClick={toggle}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {theme === 'dark' ? '☀️' : '🌙'}
        </button>
        {onSettings ? (
          <button type="button" className={styles.iconBtn} onClick={onSettings} aria-label="Settings">
            ⚙
          </button>
        ) : null}
        {onLogout ? (
          <button type="button" className={styles.iconBtn} onClick={onLogout} aria-label="Log out">
            Log out
          </button>
        ) : null}
      </header>
      <main className={styles.body}>{children}</main>
    </div>
  );
}
